/* @flow */
import React, { useState, useEffect, useRef } from 'react';
import { Row } from 'react-grid-system';
import { connect } from 'react-redux';
import {
  ActionButton,
  PageHeader,
  LoadingIndicator,
  PromptText,
  DataTable,
  Images,
  ListLargeText,
  OptionButtonGroup,
  MenuOptionsSearch,
  PageSelector,
  PopperActionButton,
} from 'listertech-commons-web';

import SideNavPage from 'components/SideNavPage';
import type { Contractor } from 'data/contractors';
import PaginationActions from '../../data/pagination/actions';
import { PAGE_SIZE } from '../../data/pagination/config';

import AssignmentsModal from './AssignmentsModal';
import AutoAcceptModal from './AutoAcceptModal';
import DeleteModal from './DeleteModal';

const ContractorsPage = (props: Props) => {

  // State to store the IDs of contractors selected via checkbox
  const [selectedContractorIds, setSelectedContractorIds] = useState([]);

  const ACTIVE_TABLE = {
    headers: ['Select', 'Contractor', 'Active Visits', 'Auto-Accept', 'Action'],

    renderers: [
      (c: Contractor) => (
        <input
          type="checkbox"
          checked={selectedContractorIds.includes(c._id)}
          onChange={(e) => handleCheckboxChange(e, c._id)}
        />
      ),
      (c: Contractor) => renderText(c.name),
      (c: Contractor) => renderText(c.visitsCount),
      (c: Contractor) =>
        renderText(c.autoAccept?.allowed ? `Tier ${c.tier}` : 'Not allowed'),
      (c: Contractor) => (
        <ExpandedContractorActions
          contractor={c}
          expandedMenuId={expandedMenuId}
          setExpandedMenuId={setExpandedMenuId}
          onDetails={handleDetails}
          onAssign={handleAssign}
          onAutoAccept={handleAutoAccept}
        />
      ),
    ],
  };

  const [agencyId, setAgencyId] = useState('');
  const [expandedMenuId, setExpandedMenuId] = useState('');
  const [selectedContractor, setSelectedContractor] = useState(null);
  //const [expandedMenuId, setExpandedMenuId] = useState('');
  const [menuType, setMenuType] = useState('');
  const menuRef = useRef(null);
  const [bulkMode, setBulkMode] = useState(false);

  const {
    loading,
    error,
    agencies,
    contractors,
    getContractors,
    setActivePage,
    history,
    paginator,
    search,
  } = props;

  useEffect(() => {
    fetchData(0, true);
  }, []);

  useEffect(() => {
    if (props.location.search !== agencyId) {
      fetchData(0, true);
      setAgencyId(props.location.search);
    }
  }, [props.location]);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setExpandedMenuId('');
      }
    }
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const handleCheckboxChange = (event, contractorId) => {
    if (event.target.checked) {
      //Add contractor to the list of selected contractors
      setSelectedContractorIds((prev) => [...prev, contractorId]);
    } else {
      //Remove contractor from the list of selected contractors
      setSelectedContractorIds((prev) =>
        prev.filter((id) => id !== contractorId)
      );
    }
  };

  // TODO: Add a button to clear all selected checkboxes later (if needed)
  const clearSelectedContractors = () => {
    setSelectedContractorIds([]);
  };

  const handleDetails = (id: string) => {
    history.push(`/contractors/${id}`);
  };

  const handleToggleMenu = (id) => {
    setExpandedMenuId(id !== expandedMenuId ? id : '');
  };

  const handleAssign = (contractor) => {
    setExpandedMenuId('');
    setSelectedContractor(contractor);
    setMenuType('assign');
  };

  const handleAutoAccept = (contractor) => {
    setExpandedMenuId('');
    setSelectedContractor(contractor);
    setMenuType('autoAccept');
  };

  const handleBulkAssign = () => {
    if (selectedContractorIds.length === 0) return;
    setBulkMode(true);
    setMenuType('assign');
  };

  const handleBulkDeleteConfirmation = () => {
    if (selectedContractorIds.length === 0) return;
    setBulkMode(true);
    setMenuType('delete');
  };

  const handlePageChange = (page) => {
    fetchData(page - 1);
  };

  const fetchData = (page: number, forceFetch: boolean, searchTerm?: string) => {
    const agency = getAgency();
    const finalSearch = searchTerm ?? props.search;

    if (!Boolean(paginator.pages[page]) || forceFetch) {
      getContractors(
        page,
        'created_at',
        true,
        finalSearch,
        agency ? agency._id : ''
      );
    } else {
      setActivePage('contractors', page);
    }
  };

  const handleSearchChange = (search: string) => {
    props.setSearch(search);
    fetchData(0, true, search);
  };

  const getAgency = () => {
    const match = new RegExp(/agency_id=(.*$)/).exec(props.location.search);

    if (!match) return null;

    const id = match[1];
    return props.agencies.find((a) => a._id === id);
  };

  const renderText = (value) => <ListLargeText>{value}</ListLargeText>;

  const ExpandedContractorActions = ({
    contractor,
    expandedMenuId,
    setExpandedMenuId,
    onDetails,
    onAssign,
    onAutoAccept,
  }) => {
    const menuRef = useRef(null);

    useEffect(() => {
      function handleOutsideClick(e) {
        if (
          menuRef.current &&
          !menuRef.current.contains(e.target) &&
          !e.target.closest('.popper-menu')
        ) {
          setExpandedMenuId('');
        }
      }
      document.addEventListener('click', handleOutsideClick);
      return () => document.removeEventListener('click', handleOutsideClick);
    }, [setExpandedMenuId]);

    const toggleMenu = () => {
      setExpandedMenuId(contractor._id !== expandedMenuId ? contractor._id : '');
    };

    return (
      <div
        ref={menuRef}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        <ActionButton onClick={() => onDetails(contractor._id)}>
          Details
        </ActionButton>
        <PopperActionButton
          icon={Images.dotsVertical}
          onClick={toggleMenu}
          className="more-action-button"
          style={{
            minWidth: '10px',
            width: '30px',
            backgroundColor:
              contractor._id === expandedMenuId ? '#A6A6A766' : undefined,
          }}
          iconStyle={{ marginRight: '0', height: '20px', width: '12px' }}
          expanded={contractor._id === expandedMenuId}
          menuClassName="popper-menu"
          menuItems={[
            { text: 'Assign', onClick: () => onAssign(contractor) },
            { text: 'Auto-Accept', onClick: () => onAutoAccept(contractor) },
          ]}
        />
      </div>
    );
  };

  const agency = getAgency();
  const ids = paginator.pages[paginator.currentPage || 0] || [];
  const filteredContractors = contractors.filter((v) => ids.includes(v._id));

  return (
    <SideNavPage>
      <Row justify="space-between">
        <PageHeader>
          {agency ? `${agency.name}'s` : ''} Contractors
        </PageHeader>
        {selectedContractorIds.length > 0 && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
            <ActionButton onClick={handleBulkAssign}>
              Bulk Assign
            </ActionButton>
            <ActionButton onClick={handleBulkDeleteConfirmation}>
              Delete selected
            </ActionButton>
            <ActionButton onClick={clearSelectedContractors}>
              Clear Selection
            </ActionButton>
          </div>
        )}
        <OptionButtonGroup>
          <MenuOptionsSearch
            initialValue={search}
            onChange={handleSearchChange}
          />
        </OptionButtonGroup>
      </Row>

      {loading ? (
        <LoadingIndicator />
      ) : error ? (
        <PromptText>{error}</PromptText>
      ) : (
        <DataTable
          headers={ACTIVE_TABLE.headers}
          renderers={ACTIVE_TABLE.renderers}
          data={filteredContractors}
          onSort={undefined}
        />
      )}

      <Row justify="flex-end" style={{ marginRight: 20 }}>
        {paginator.objectCount && paginator.pagesCount ? (
          <PageSelector
            total={paginator.objectCount}
            limit={PAGE_SIZE}
            pageCount={paginator.pagesCount}
            currentPage={paginator.currentPage + 1 || 1}
            onPageChange={handlePageChange}
          />
        ) : null}
      </Row>

      {menuType === 'assign' && (
        <AssignmentsModal
          contractor={bulkMode ? contractors.filter(c => selectedContractorIds.includes(c._id)) : selectedContractor}
          onClose={() => {
            setSelectedContractor(null);
            setBulkMode(false);
            setMenuType('');
            clearSelectedContractors();
            fetchData(paginator.currentPage, true);
          }}
        />
      )}

      {menuType === 'delete' && (
        <DeleteModal
          contractors={bulkMode ? contractors.filter(c => selectedContractorIds.includes(c._id)) : selectedContractor}
          onClose={() => {
            setSelectedContractor(null);
            setMenuType('');
            clearSelectedContractors();
            fetchData(paginator.currentPage, true);
          }}
        />
      )}

      {menuType === 'autoAccept' && selectedContractor && (
        <AutoAcceptModal
          contractor={selectedContractor}
          onClose={() => {
            setSelectedContractor(null);
            setMenuType('');
            fetchData(paginator.currentPage, true);
          }}
        />
      )}

    </SideNavPage>
  );
};

const mapStateToProps = (state) => ({
  loading: state.loading['GET_CONTRACTORS'],
  error: state.error['GET_CONTRACTORS'],
  contractors: state.contractors.all,
  search: state.contractors.search,
  paginator: state.pagination.contractors,
  agencies: state.agency.all,
});

const mapDispatchToProps = (dispatch) => ({
  getContractors: (page, sort, desc, search, agencyId) =>
    dispatch({ type: 'GET_CONTRACTORS', page, sort, desc, search, agencyId }),
  setSearch: (search) => dispatch({ type: 'SET_CONTRACTORS_SEARCH', search }),
  setActivePage: (key, page) =>
    dispatch(PaginationActions.setActivePage(key, page)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContractorsPage);

